/**
 * The little constructors `src/config/level.ts` is written with.
 *
 * They exist so the level reads as a list of things rather than as a list of
 * six-number box literals: `solid('nimi', x, z, w, d, h)` says where a thing
 * is and how big it is, which is how somebody placing it thinks about it.
 */

import { DOOR_HANDLE } from '../config/gameplay';
import type { Vec3 } from '../utils/math';
import type { Box, MaterialKey } from './levelBox';
import type { CylinderSpec, HandleSpec, HoseSpec, ScreenContent, ScreenSpec } from './levelProps';
import type { DecorSpec, FloorSpec, SolidSpec } from './levelSolids';

/** Builds a box from XZ extents plus a floor height and a height. */
export function ext(
  minX: number,
  minZ: number,
  maxX: number,
  maxZ: number,
  height: number,
  y = 0,
): Box {
  return { minX, minY: y, minZ, maxX, maxY: y + height, maxZ };
}

export function solid(
  name: string,
  material: MaterialKey,
  minX: number,
  minZ: number,
  maxX: number,
  maxZ: number,
  height: number,
  y = 0,
): SolidSpec {
  return { name, material, box: ext(minX, minZ, maxX, maxZ, height, y) };
}

export function decor(
  name: string,
  material: MaterialKey,
  minX: number,
  minZ: number,
  maxX: number,
  maxZ: number,
  height: number,
  y = 0,
): DecorSpec {
  return { name, material, box: ext(minX, minZ, maxX, maxZ, height, y) };
}

/**
 * The painike on a leaf, worked out from the leaf itself.
 *
 * Two facts are all it takes, and neither of them can be read off a box:
 * `hingeEnd` is which end of the leaf's long axis it is hung on (-1 low, +1
 * high) and `face` is which of its two faces the handle is screwed to (again
 * -1 low, +1 high, across the thin axis). Everything else follows from the
 * leaf and from `DOOR_HANDLE` - a handle is a hand's width in from the edge
 * you pull, at the height a hand is, and its lever reaches back towards the
 * hinge because that is the way you push it.
 *
 * Derived rather than typed so that moving a leaf moves what is on it, which
 * is the same reason `boxCenter` exists: a door and its handle drifting apart
 * by a centimetre is a handle floating off a door.
 */
export function handle(name: string, leaf: Box, hingeEnd: number, face: number): HandleSpec {
  const alongZ = leaf.maxX - leaf.minX < leaf.maxZ - leaf.minZ;
  // The leaf's own axes: the long one it is hinged along, and the thin one it
  // has two faces on.
  const long = alongZ ? { min: leaf.minZ, max: leaf.maxZ } : { min: leaf.minX, max: leaf.maxX };
  const thin = alongZ ? { min: leaf.minX, max: leaf.maxX } : { min: leaf.minZ, max: leaf.maxZ };
  // In from the free edge, which is whichever end the hinge is not on.
  const along =
    hingeEnd > 0 ? long.min + DOOR_HANDLE.backset : long.max - DOOR_HANDLE.backset;
  const across = face > 0 ? thin.max : thin.min;
  const at = alongZ
    ? { x: across, y: DOOR_HANDLE.height, z: along }
    : { x: along, y: DOOR_HANDLE.height, z: across };

  // Which way the face looks, and then which way the handle's own +X lands
  // once it has been turned that far. The lever reaches towards the hinge, so
  // `reach` is whichever sign of that axis points at it - and it comes out
  // opposite on the two faces of one leaf, because a pair of handles on a door
  // are mirror images and not copies.
  const normal = alongZ ? { x: face, z: 0 } : { x: 0, z: face };
  const rotationY = Math.atan2(normal.x, normal.z);
  const localX = { x: Math.cos(rotationY), z: -Math.sin(rotationY) };
  const towardsHinge = alongZ ? { x: 0, z: hingeEnd } : { x: hingeEnd, z: 0 };
  const reach = Math.round(localX.x * towardsHinge.x + localX.z * towardsHinge.z);

  return { name, at, rotationY, reach };
}

export function floor(
  name: string,
  material: MaterialKey,
  minX: number,
  minZ: number,
  maxX: number,
  maxZ: number,
  y = 0,
  rotationY = 0,
): FloorSpec {
  return {
    name,
    material,
    center: [(minX + maxX) / 2, (minZ + maxZ) / 2],
    size: [maxX - minX, maxZ - minZ],
    y,
    rotationY,
  };
}

export function cylinder(
  name: string,
  material: MaterialKey,
  cx: number,
  cz: number,
  radius: number,
  height: number,
  y = 0,
  extra: Partial<CylinderSpec> = {},
): CylinderSpec {
  return { name, material, center: [cx, cz], radius, height, y, ...extra };
}

/**
 * The picture on a monitor whose front face is the plane `x = faceX`.
 *
 * `side` is the direction the seat is in: -1 for a machine you sit west of,
 * +1 for one you sit east of. The picture is inset by a bezel and stood a
 * centimetre proud of the face, because two surfaces on the same plane fight
 * for the same pixels and a flickering monitor is the one thing in a room full
 * of monitors the eye will go straight to.
 */
export function monitorScreen(
  name: string,
  content: ScreenContent,
  faceX: number,
  cz: number,
  width: number,
  bottom: number,
  height: number,
  side: -1 | 1,
): ScreenSpec {
  const bezel = 0.03;
  return {
    name,
    content,
    center: { x: faceX + side * 0.01, y: bottom + height / 2, z: cz },
    width: width - 2 * bezel,
    height: height - 2 * bezel,
    rotationY: (side * Math.PI) / 2,
  };
}

export function hose(
  name: string,
  material: MaterialKey,
  radius: number,
  points: Vec3[],
): HoseSpec {
  return { name, material, radius, points };
}
